import React, { useEffect, useState } from 'react';
import { X, ExternalLink, Calendar, User, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { Tables } from '@/integrations/supabase/types';

export interface AddedToDisplayModalProps {
  isOpen: boolean;
  onClose: () => void;
  sequenceId: string;
  sequenceTitle: string;
  sequenceArtist: string;
  /** Callback triggered after user navigates to profile or dismisses modal */
  onAfterAction?: () => void;
}

interface DisplayListEntry {
  id: string;
  sequence_id: string;
  year: number;
  added_at: string;
}

interface UserPreferences {
  suppress_added_to_display_modal: boolean;
  default_charity_id?: string;
}

/**
 * Modal component that appears after sequence download to confirm addition to user's display list.
 * Provides navigation to profile and option to suppress future instances.
 */
export const AddedToDisplayModal: React.FC<AddedToDisplayModalProps> = ({
  isOpen,
  onClose,
  sequenceId,
  sequenceTitle,
  sequenceArtist,
  onAfterAction
}) => {
  const { user } = useAuth();
  const { markAddedToDisplayModalShown } = useDownloadFlowStore();
  const [isAddingToList, setIsAddingToList] = useState(false);
  const [hasAddedToList, setHasAddedToList] = useState(false);
  const [suppressFuture, setSuppressFuture] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [userHasDisplay, setUserHasDisplay] = useState<boolean | null>(null);
  const currentYear = new Date().getFullYear();

  // Load user preferences on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('suppress_added_to_display_modal, default_charity_id')
          .eq('user_id', user.id)
          .single();

        // Handle table not found error gracefully
        if (error && error.code === '42P01') {
          console.warn('user_preferences table does not exist, using default preferences');
          setUserPreferences({ suppress_added_to_display_modal: false });
          return;
        }

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading user preferences:', error);
          return;
        }

        setUserPreferences(data || { suppress_added_to_display_modal: false });
      } catch (error) {
        console.error('Failed to load user preferences:', error);
      }
    };

    if (isOpen) {
      loadUserPreferences();
    }
  }, [isOpen, user?.id]);

  // Check if user has any displays configured
  useEffect(() => {
    const checkUserDisplays = async () => {
      if (!user?.id || !isOpen) return;

      try {
        const { data, error } = await supabase
          .from('displays')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        // Handle table not found error gracefully
        if (error && error.code === '42P01') {
          console.warn('displays table does not exist, assuming no displays');
          setUserHasDisplay(false);
          return;
        }

        if (error) {
          console.error('Error checking user displays:', error);
          setUserHasDisplay(false);
          return;
        }

        setUserHasDisplay(data && data.length > 0);
      } catch (error) {
        console.error('Failed to check user displays:', error);
        setUserHasDisplay(false);
      }
    };

    if (isOpen) {
      checkUserDisplays();
    }
  }, [isOpen, user?.id]);

  // Add sequence to user's display list
  useEffect(() => {
    const addToDisplayList = async () => {
      if (!isOpen || !user?.id || hasAddedToList) return;

      setIsAddingToList(true);

      try {
        // Check if sequence is already in current year's display list
        const { data: existingEntry, error: checkError } = await supabase
          .from('user_display_lists')
          .select('id')
          .eq('user_id', user.id)
          .eq('sequence_id', sequenceId)
          .eq('year', currentYear)
          .single();

        // Handle table not found error gracefully
        if (checkError && checkError.code === '42P01') {
          console.warn('user_display_lists table does not exist, skipping display list functionality');
          setHasAddedToList(true); // Mark as completed to avoid retry
          return;
        }

        if (checkError && checkError.code !== 'PGRST116') {
          throw checkError;
        }

        // Only add if not already present
        if (!existingEntry) {
          const { error: insertError } = await supabase
            .from('user_display_lists')
            .insert({
              user_id: user.id,
              sequence_id: sequenceId,
              year: currentYear
            });

          // Handle table not found error gracefully
          if (insertError && insertError.code === '42P01') {
            console.warn('user_display_lists table does not exist, skipping display list functionality');
            setHasAddedToList(true); // Mark as completed to avoid retry
            return;
          }

          if (insertError) {
            throw insertError;
          }
        }

        setHasAddedToList(true);
      } catch (error) {
        console.error('Error adding sequence to display list:', error);
        toast.error('Failed to add sequence to your display list');
      } finally {
        setIsAddingToList(false);
      }
    };

    addToDisplayList();
  }, [isOpen, user?.id, sequenceId, currentYear, hasAddedToList]);

  // Handle modal close and mark as shown in download flow
  const handleClose = async () => {
    await handleSavePreferences();
    markAddedToDisplayModalShown();
    onClose();
    onAfterAction?.();
  };

  // Navigate to user profile display list or songs tab based on user's display status
  const handleTakeMeThere = async () => {
    await handleSavePreferences();
    markAddedToDisplayModalShown();
    
    // Navigate to appropriate tab based on whether user has displays
    const tab = userHasDisplay ? 'display' : 'songs';
    const profileUrl = userHasDisplay 
      ? `/profile?tab=display&year=${currentYear}` 
      : `/profile?tab=songs`;
    window.open(profileUrl, '_blank');
    
    onClose();
    onAfterAction?.();
  };

  // Save user preferences for suppressing future modals
  const handleSavePreferences = async () => {
    if (!user?.id || !suppressFuture || isSavingPreferences) return;

    setIsSavingPreferences(true);

    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          suppress_added_to_display_modal: true,
          default_charity_id: userPreferences?.default_charity_id || null
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error saving user preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !user) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="added-to-display-title"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <List className="h-5 w-5 text-green-600" />
            </div>
            <h2 id="added-to-display-title" className="text-xl font-semibold text-gray-900">
              {userHasDisplay ? 'Added to Display!' : 'Download Complete!'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success Message */}
          <div className="text-center space-y-2">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              "{sequenceTitle}" has been {userHasDisplay ? `added to your ${currentYear} display list` : 'downloaded successfully'}!
            </h3>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <User className="h-4 w-4" />
              <span>{sequenceArtist}</span>
            </div>
          </div>

          {/* Status */}
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              {isAddingToList ? (
                <>
                  <div className="h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-green-800">Adding to your display list...</span>
                </>
              ) : hasAddedToList ? (
                <>
                  <div className="h-4 w-4 rounded-full bg-green-600 flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-green-800">
                    {userHasDisplay 
                      ? `Successfully added to your ${currentYear} display!`
                      : 'Successfully added to your downloads!'
                    }
                  </span>
                </>
              ) : (
                <span className="text-green-800">Ready to add to your display list</span>
              )}
            </div>
          </div>

          {/* Don't Show Again Option */}
          {userPreferences && !userPreferences.suppress_added_to_display_modal && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="suppress-future"
                  checked={suppressFuture}
                  onCheckedChange={(checked) => setSuppressFuture(checked as boolean)}
                />
                <div className="space-y-1">
                  <label 
                    htmlFor="suppress-future" 
                    className="text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    Don't show this message again
                  </label>
                  <p className="text-xs text-gray-600">
                    Future downloads will still be processed normally, but this confirmation won't appear.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 p-6 pt-0">
          <Button
            onClick={handleTakeMeThere}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            disabled={isAddingToList || isSavingPreferences}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {userHasDisplay ? 'View My Display' : 'View My Songs'}
          </Button>
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full"
            disabled={isSavingPreferences}
          >
            {isSavingPreferences ? 'Saving preferences...' : 'Thanks, Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddedToDisplayModal; 